import { CheckCircle, RotateCcw } from 'lucide-react'
import type { Delivery, DeliveryStatus } from '@/types/database'
import { deliveryActions } from '@/lib/deliveryStatus'
import { Button } from '@/components/ui'

// Delivery action panel — renders the forward-transition buttons for a
// delivery's current status. The state machine lives in deliveryActions
// (src/lib/deliveryStatus.ts) so it's unit-testable; this component just renders
// it (and is itself covered by DeliveryActionButtons.test.tsx). Extracted from
// Deliveries.tsx's former inline ActionButtons, behaviour-preserving.
export function DeliveryActionButtons({ delivery: d, onTransition, busy = false, saving = busy, error = null, done = null }: {
  delivery: Pick<Delivery, 'status' | 'intake_path'>
  onTransition: (to: DeliveryStatus, reason: string) => void
  /** A status write is running: lock the buttons so a second tap is not refused as stale. */
  busy?: boolean
  /** THIS delivery is the one being saved: only it says "Saving…". Defaults to busy. */
  saving?: boolean
  /** The last status tap for this delivery failed: say so, in words. */
  error?: string | null
  /** The last tap worked: the status it moved to, shown as a tick (UF-01). */
  done?: string | null
}) {
  const actions = deliveryActions(d.status, d.intake_path)
  // A tap that worked moves the card to a status with no action left: keep the tick.
  if (actions.length === 0 && !done) return null
  const multi = actions.length > 1
  return (
    <div>
    <div className={multi ? 'flex gap-2' : undefined}>
      {actions.map((a) => (
        <Button
          key={a.to}
          variant="ghost" size="sm"
          className={multi ? 'flex-1' : 'w-full'}
          disabled={busy}
          onClick={() => onTransition(a.to, a.reason)}
        >
          {a.icon === 'check' && <CheckCircle size={14} />}
          {a.icon === 'rotate' && <RotateCcw size={14} />}
          {a.label}
        </Button>
      ))}
    </div>
    {saving && <p role="status" className="mt-2 text-caption text-fg-muted">Saving…</p>}
    {done && !saving && !error && (
      <p role="status" className="mt-2 text-caption text-success flex items-center gap-1">
        <CheckCircle size={12} /> Moved to {done}
      </p>
    )}
    {error && (
      <p role="alert" className="mt-2 rounded-lg bg-danger-soft border border-danger-edge px-3 py-2 text-sm text-danger">
        {error}
      </p>
    )}
    </div>
  )
}
